import { BffItemCatalogo } from './types';

/**
 * Mapa nombre -> id de los catálogos de vehículo.
 *
 * Los selects del cotizador trabajan con NOMBRES (el monolito devolvía listas
 * de strings). El BFF, en cambio, pide ids en `POST contacto`. Para no tocar
 * los componentes, cada servicio de catálogo registra acá el par nombre->id de
 * lo que acaba de traer, y `resolverIds` lo recupera al armar el contacto.
 *
 * Se persiste en sessionStorage: el paso de datos y el de precios son montajes
 * distintos, y precios necesita los ids de lo que se eligió antes.
 */

type Mapa = Record<string, number>;

interface CatalogoPersistido {
  marcas: Mapa;
  modelos: Mapa;
  versiones: Mapa;
  /** Ids resueltos por el datacar (patente): mandan sobre los mapas. */
  patente?: { marcaId: number; modeloId: number; versionId: number };
}

const CLAVE = 'auto_bff_catalogo_vehiculos';

const vacio = (): CatalogoPersistido => ({
  marcas: {},
  modelos: {},
  versiones: {},
});

const normalizar = (nombre?: string): string =>
  (nombre || '').trim().toUpperCase();

const leer = (): CatalogoPersistido => {
  try {
    const crudo = window.sessionStorage.getItem(CLAVE);
    if (!crudo) return vacio();
    return { ...vacio(), ...JSON.parse(crudo) };
  } catch {
    return vacio();
  }
};

const escribir = (catalogo: CatalogoPersistido): void => {
  try {
    window.sessionStorage.setItem(CLAVE, JSON.stringify(catalogo));
  } catch {
    /* sessionStorage no disponible: se sigue sin cache */
  }
};

const registrar = (
  clave: 'marcas' | 'modelos' | 'versiones',
  items: BffItemCatalogo[]
): void => {
  const catalogo = leer();
  (items || []).forEach((item) => {
    catalogo[clave][normalizar(item.nombre)] = item.id;
  });
  escribir(catalogo);
};

export const registrarMarcas = (items: BffItemCatalogo[]): void =>
  registrar('marcas', items);

export const registrarModelos = (items: BffItemCatalogo[]): void =>
  registrar('modelos', items);

export const registrarVersiones = (items: BffItemCatalogo[]): void =>
  registrar('versiones', items);

/** Ids que resolvió el datacar por patente. Tienen prioridad sobre los mapas. */
export const registrarPatente = (ids: {
  marcaId: number;
  modeloId: number;
  versionId: number;
}): void => {
  const catalogo = leer();
  catalogo.patente = ids;
  escribir(catalogo);
};

export const limpiarCatalogo = (): void => {
  try {
    window.sessionStorage.removeItem(CLAVE);
  } catch {
    /* noop */
  }
};

/** Ids que dejó el datacar, si la cotización entró por patente. */
export const idsDePatente = ():
  | { marcaId: number; modeloId: number; versionId: number }
  | undefined => leer().patente;

export const idDeMarca = (nombre?: string): number | undefined =>
  leer().marcas[normalizar(nombre)];

export const idDeModelo = (nombre?: string): number | undefined =>
  leer().modelos[normalizar(nombre)];

export const idDeVersion = (nombre?: string): number | undefined =>
  leer().versiones[normalizar(nombre)];

export interface IdsVehiculo {
  marcaId: number;
  modeloId: number;
  versionId: number;
}

/**
 * Ids del vehículo elegido. Si el vehículo vino del datacar se usan esos ids
 * (los nombres pueden no coincidir exactamente con los del catálogo); si no,
 * se resuelven por nombre contra lo que se registró en los selects.
 */
export const resolverIds = (nombres: {
  marca?: string;
  modelo?: string;
  version?: string;
}): IdsVehiculo => {
  const catalogo = leer();
  const porPatente = catalogo.patente;
  return {
    marcaId:
      catalogo.marcas[normalizar(nombres.marca)] ?? porPatente?.marcaId ?? 0,
    modeloId:
      catalogo.modelos[normalizar(nombres.modelo)] ?? porPatente?.modeloId ?? 0,
    versionId:
      catalogo.versiones[normalizar(nombres.version)] ??
      porPatente?.versionId ??
      0,
  };
};
